// Service for the employee active contract screen
// GET /v{version}/employees/{employeeId}/active-contract
// PUT /v{version}/employees/{employeeId}/active-contract/{employeeContractId}
// Both actions live in a dedicated backend controller that acts as a permission facade: they are
// authorized against the "Condiciones laborales" section of the collaborators interface instead of
// the Contracts interface, while delegating to the same business services.
import { apiClient, API_CONFIG } from '@/api/core';
import type { ApiResponse } from '@/api/shared';
import type { LC_GetActiveEmployeeContractOut } from '../types/employeeContractActive.types';
import type {
  LC_UpdateEmployeeContractIn,
  LC_UpdateEmployeeContractOut,
} from '../types/employeeContractEdit.types';


/**
 * Service for employee active contract operations
 */
export class EmployeeContractActiveService {
  /**
   * GET the last active contract of an employee
   * Responds 404 when the employee has no active contract, which the caller must treat as an
   * empty state rather than as a failure.
   *
   * @param employeeId - Employee ID
   * @param signal - Optional abort signal
   * @returns ApiResponse with the active contract details
   */
  async getActive(
    employeeId: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LC_GetActiveEmployeeContractOut>> {
    const endpoint = API_CONFIG.ENDPOINTS.EMPLOYEE_CONTRACT.ACTIVE(employeeId);
    return apiClient.get<LC_GetActiveEmployeeContractOut>(endpoint, { signal });
  }

  /**
   * PUT the active contract of an employee
   * Takes the same payload as the contract update endpoint — the backend facade delegates to the
   * very same update service, so business validations and audit behave identically.
   *
   * @param employeeId - Employee ID
   * @param employeeContractId - Identifier returned by getActive
   * @param data - Updated contract data
   * @returns ApiResponse with the updated contract number and name
   */
  async updateActive(
    employeeId: number,
    employeeContractId: number,
    data: LC_UpdateEmployeeContractIn,
  ): Promise<ApiResponse<LC_UpdateEmployeeContractOut>> {
    const endpoint = API_CONFIG.ENDPOINTS.EMPLOYEE_CONTRACT.ACTIVE_UPDATE(
      employeeId,
      employeeContractId,
    );
    return apiClient.put<LC_UpdateEmployeeContractOut>(endpoint, data);
  }
}

export const employeeContractActiveService = new EmployeeContractActiveService();
